import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/firestore';

interface MaintenanceContextType {
  isMaintenanceMode: boolean;
  canAccessForge: boolean;
  maintenanceMessage: string;
}

const defaultMaintenanceMessage =
  'Sorry, the Forges are down for maintenance right now! Please check back in a little while.';

// Create the context with default values
const MaintenanceContext = createContext<MaintenanceContextType>({
  isMaintenanceMode: true, // Default to maintenance mode enabled
  canAccessForge: false,
  maintenanceMessage: defaultMaintenanceMessage,
});

export const useMaintenanceMode = () => useContext(MaintenanceContext);

export const MaintenanceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState<boolean>(false); // Default to maintenance mode disabled
  const [maintenanceMessage, setMaintenanceMessage] = useState<string>(defaultMaintenanceMessage);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const { currentUser } = useAuth();

  // Check if the current user is an admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!currentUser) {
        setIsAdmin(false);
        return;
      }

      try {
        // Check if the user is the hardcoded admin UID
        if (currentUser.uid === 'tOCaPaZ9XDb3eBXgoNYzGPWeyY2') {
          setIsAdmin(true);
          return;
        }

        // Check if the user has isAdmin: true in their document
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists() && userDoc.data().isAdmin === true) {
          setIsAdmin(true);
        } else {
          setIsAdmin(false);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, [currentUser]);

  // Determine if the current user can access the forge
  const canAccessForge = !isMaintenanceMode || isAdmin;

  // Fetch maintenance status from Firestore - only if user is logged in
  useEffect(() => {
    // Default to maintenance mode disabled until we can check
    setIsMaintenanceMode(false);

    const fetchMaintenanceStatus = async () => {
      // Only try to fetch if user is logged in
      if (!currentUser) return;

      try {
        const maintenanceRef = doc(db, 'settings', 'maintenance');
        const maintenanceDoc = await getDoc(maintenanceRef);

        if (maintenanceDoc.exists()) {
          const data = maintenanceDoc.data();
          setIsMaintenanceMode(data.enabled === true);

          if (data.message) {
            setMaintenanceMessage(data.message);
          }
        } else {
          // If document doesn't exist, maintenance mode is off
          setIsMaintenanceMode(false);
        }
      } catch (error) {
        // On error, don't show maintenance mode
        setIsMaintenanceMode(false);
        console.error('Error fetching maintenance status:', error);
      }
    };

    // Only set up interval if user is logged in
    if (currentUser) {
      fetchMaintenanceStatus();

      // Set up a listener to check every minute (in case maintenance mode is toggled)
      const intervalId = setInterval(fetchMaintenanceStatus, 60000);
      return () => clearInterval(intervalId);
    }
  }, [currentUser]); // Re-run when user changes

  const value = {
    isMaintenanceMode,
    canAccessForge,
    maintenanceMessage,
  };

  return <MaintenanceContext.Provider value={value}>{children}</MaintenanceContext.Provider>;
};
